// Same single-char operator set core's own israngeseleciton gates on
// (operatorArr in the bundle). "=" is
// included: it also covers the two-char variants ("==", "<=", ">=", "<>",
// "!=") since every one of them ENDS in a character already in this set.
const OPERATOR_CHARS = new Set(['=', '+', '-', '>', '<', '/', '*', '%', '&', '^']);

/**
 * Pure mirror of core's `israngeseleciton` (DOM-selection-based in
 * the library; here driven off plain text+caret so it's unit-testable).
 * True only when the caret sits right after '(', ',', or an operator, AND is
 * not inside an unterminated string literal (a run of an odd number of
 * unescaped double-quotes before the caret).
 */
export const isRefInsertablePosition = (text: string, caret: number): boolean => {
    const upToCaret = text.slice(0, caret);
    const quoteCount = (upToCaret.match(/"/g) ?? []).length;
    if (quoteCount % 2 === 1) return false;
    const last = upToCaret[upToCaret.length - 1];
    if (last === undefined) return false;
    return last === '(' || last === ',' || OPERATOR_CHARS.has(last);
};

export type ArrowKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';

export interface PointCell {
    r: number;
    c: number;
}

const ARROW_DELTA: Record<ArrowKey, [number, number]> = {
    ArrowUp: [-1, 0],
    ArrowDown: [1, 0],
    ArrowLeft: [0, -1],
    ArrowRight: [0, 1],
};

const clamp = (n: number, max: number): number => Math.min(max, Math.max(0, n));

/**
 * One arrow-key step of formula point-mode. Without Shift, the arrow
 * always moves ONE cell from wherever the point currently is, and starts a
 * fresh single-cell point (anchor snaps to the new cell too). With Shift,
 * only the cursor moves — the anchor stays put, growing the range. Both
 * axes clamp to the sheet's bounds (row/col 0 is the first row/column).
 */
export const nextPointCell = (
    anchor: PointCell,
    cursor: PointCell,
    key: ArrowKey,
    shift: boolean,
    bounds: { rows: number; cols: number },
): { anchor: PointCell; cursor: PointCell } => {
    const [dr, dc] = ARROW_DELTA[key];
    const nextCursor: PointCell = {
        r: clamp(cursor.r + dr, Math.max(0, bounds.rows - 1)),
        c: clamp(cursor.c + dc, Math.max(0, bounds.cols - 1)),
    };
    return shift ? { anchor, cursor: nextCursor } : { anchor: nextCursor, cursor: nextCursor };
};
